import { useEffect, useState, type ReactNode } from 'react'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { usePermissions } from '@/shared/auth/usePermissions'
import { fetchSubscriber, revealSubscriberPhone } from '@/features/subscriber/subscriberApi'
import { fetchContractTemplate, fetchPerson, fetchSubscriberPlace, type Contract } from './contractApi'
import { ContractPrintPortal, type ContractPrintData } from './ContractPrint'
import { contractImagesSettled } from './contractPrint'

export interface ContractPrinter {
  print: (contract: Contract) => void
  loading: boolean
  portal: ReactNode
}

/** Shartnoma ma'lumotini yig'ib, A4 sahifani chop etadi; telefon faqat `contact.reveal` bilan, auditga yoziladi. */
export function useContractPrinter(): ContractPrinter {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const { can } = usePermissions()
  const [data, setData] = useState<ContractPrintData | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (data === null) return
    let cancelled = false
    const run = async () => {
      await new Promise((resolve) => requestAnimationFrame(resolve))
      await contractImagesSettled()
      if (cancelled) return
      window.print()
      setData(null)
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [data])

  const print = (contract: Contract) => {
    setLoading(true)
    void (async () => {
      try {
        const [subscriber, person, place, template] = await Promise.all([
          fetchSubscriber(contract.subscriberId),
          fetchPerson(contract.subscriberId),
          fetchSubscriberPlace(contract.subscriberId),
          fetchContractTemplate(),
        ])
        const phone = can('contact.reveal')
          ? (await revealSubscriberPhone(contract.subscriberId, t('contract.printRevealReason', { number: contract.number })))
              .phone
          : null
        const profile = subscriber.profile
        setData({
          contract,
          person,
          place,
          subscriber: {
            customerId: profile?.customerId ?? null,
            fullName: [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') || subscriber.login,
            fatherName: profile?.fatherName ?? null,
            phone,
          },
          terms: template.terms ?? null,
          letterheadUrl: template.letterheadUrl ?? null,
          companyName: null,
        })
      } catch (error) {
        void message.error(translateError(error, t))
      } finally {
        setLoading(false)
      }
    })()
  }

  return { print, loading, portal: data ? <ContractPrintPortal data={data} /> : null }
}
